/**
 * The public waitlist: POST /api/public/waitlist. No account and no Authorization header, ever: this runs for
 * people who aren't signed in, and the server keys its rate limits on the connection, not on a token.
 *
 * The server answers 202 {"ok": true} the same way for a new email, a repeat, someone already invited and a
 * submission it judged suspicious, so the page can never tell anyone who else is on the list. The only answers
 * that differ are the ones about the form itself (400 bad email, 422 one bad field) and 429 (too many tries).
 *
 * Where someone came from (utm_* tags and an outside referrer) is read once, on the first page they open, and kept
 * in this tab's sessionStorage until they reach the form; by then document.referrer is bearri.co itself.
 *
 * `elapsed_ms` (how long the page had the form open before it was sent) lets the server file a submission faster
 * than a person types as 'suspect' (kept, never dropped): W1's "too-fast submissions".
 */
import apiConfig from '../config/apiConfig';
import {
  LIMITS,
  PLAN_INTERESTS,
  type AudienceBand,
  type HelpTopic,
  type PlanInterest,
  type WaitlistPlatform,
} from '../components/auth/waitlistOptions';

export const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'] as const;
type UtmKey = typeof UTM_KEYS[number];

export type WaitlistSource = Partial<Record<UtmKey, string>> & { referrer?: string };

export type WaitlistRequest = {
  first_name: string;
  email: string;
  platform: WaitlistPlatform;
  channel: string;
  audience_band: AudienceBand;
  help_with: HelpTopic[];
  help_other?: string;
  plan_interest?: PlanInterest;
  source?: WaitlistSource;
  /** the honeypot: a person never sees or fills it */
  bearri_check: string;
  /** milliseconds from the form first showing to this send (the server's too-fast check) */
  elapsed_ms?: number;
};

/** What the form holds while someone types: choices start unpicked (''), never pre-filled for them. */
export type WaitlistValues = {
  first_name: string;
  email: string;
  platform: WaitlistPlatform | '';
  channel: string;
  audience_band: AudienceBand | '';
  help_with: HelpTopic[];
  help_other: string;
  bearri_check: string;
};

export const EMPTY_WAITLIST_VALUES: WaitlistValues = {
  first_name: '',
  email: '',
  platform: '',
  channel: '',
  audience_band: '',
  help_with: [],
  help_other: '',
  bearri_check: '',
};

export const SOURCE_KEY = 'bearri.arrival';
const REFERRER_MAX = 300;
const UTM_MAX = 100;

export const WAITLIST_URL = () => `${apiConfig.baseUrl}/api/public/waitlist`;

/** unreachable: the request never got an answer (offline, blocked). server: an answer we can't use (5xx, junk). */
export type WaitlistErrorKind = 'bad_email' | 'bad_field' | 'rate_limited' | 'unreachable' | 'server';
/** which of the two IP limits a 429 hit (the server names it: "5 per 1 minute" / "30 per 1 day") */
export type RateWindow = 'minute' | 'day' | 'unknown';

export class WaitlistError extends Error {
  kind: WaitlistErrorKind;
  /** the request field the server named (422), or 'email' (400) */
  field?: string;
  /** 429 only */
  window?: RateWindow;
  constructor(kind: WaitlistErrorKind, message = '', field?: string, window?: RateWindow) {
    super(message);
    this.name = 'WaitlistError';
    this.kind = kind;
    this.field = field;
    this.window = window;
  }
}

/** The request body, from what the form holds: trimmed, with "Something else" text only when that tag is picked. */
export function buildWaitlistRequest(
  values: WaitlistValues,
  extras: { planInterest?: PlanInterest; source?: WaitlistSource; elapsedMs?: number } = {},
): WaitlistRequest {
  const helpOther = values.help_with.includes('other') ? values.help_other.trim() : '';
  const body: WaitlistRequest = {
    first_name: values.first_name.trim(),
    email: values.email.trim(),
    platform: values.platform as WaitlistPlatform,
    channel: values.channel.trim(),
    audience_band: values.audience_band as AudienceBand,
    help_with: [...values.help_with],
    bearri_check: values.bearri_check,
  };
  if (helpOther) body.help_other = helpOther.slice(0, LIMITS.help_other);
  if (extras.planInterest) body.plan_interest = extras.planInterest;
  if (extras.source && Object.keys(extras.source).length) body.source = extras.source;
  if (typeof extras.elapsedMs === 'number' && Number.isFinite(extras.elapsedMs)) {
    body.elapsed_ms = Math.max(0, Math.round(extras.elapsedMs));
  }
  return body;
}

type ErrorDetail = { code?: string; field?: string; message?: string };
type FastApiDetail = Array<{ loc?: unknown[]; msg?: string }>;

/** The response's `detail`, or null when the body isn't the JSON we expect. */
async function readDetail(res: Response): Promise<ErrorDetail | FastApiDetail | null> {
  try {
    const data: unknown = await res.json();
    if (!data || typeof data !== 'object') return null;
    const detail = (data as { detail?: unknown }).detail;
    if (Array.isArray(detail)) return detail as FastApiDetail;
    return detail && typeof detail === 'object' ? (detail as ErrorDetail) : null;
  } catch {
    return null;
  }
}

/** "5 per 1 minute" or "30 per 1 day", from the 429's {"error": "Rate limit exceeded: …"} */
async function readWindow(res: Response): Promise<RateWindow> {
  try {
    const data: unknown = await res.json();
    const said = data && typeof data === 'object' ? (data as { error?: unknown; detail?: unknown }) : {};
    const text = [said.error, said.detail].filter((v) => typeof v === 'string').join(' ');
    if (/\bday\b/i.test(text)) return 'day';
    if (/\bminute\b/i.test(text)) return 'minute';
  } catch {
    // not JSON: we can't tell which limit it was
  }
  return 'unknown';
}

export async function joinWaitlist(body: WaitlistRequest): Promise<void> {
  let res: Response;
  try {
    res = await fetch(WAITLIST_URL(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'omit',
      body: JSON.stringify(body),
    });
  } catch {
    throw new WaitlistError('unreachable');
  }
  if (res.ok) return;
  if (res.status === 429) throw new WaitlistError('rate_limited', '', undefined, await readWindow(res));
  if (res.status === 400) {
    const detail = await readDetail(res);
    const message = detail && !Array.isArray(detail) ? detail.message : undefined;
    throw new WaitlistError('bad_email', message || '', 'email');
  }
  if (res.status === 422) {
    const detail = await readDetail(res);
    if (Array.isArray(detail)) {
      // FastAPI's own validation shape, in case a request ever misses the endpoint's own checks
      const loc = detail[0]?.loc;
      const field = Array.isArray(loc) ? String(loc[loc.length - 1] ?? '') : '';
      throw new WaitlistError('bad_field', '', field || undefined);
    }
    throw new WaitlistError('bad_field', detail?.message || '', detail?.field || undefined);
  }
  // a 5xx or a body we can't read: the request arrived, so the server may already have kept it
  throw new WaitlistError('server');
}

/** ?plan=free|plus|pro from a pricing card, or nothing. */
export function planFromSearch(search: string): PlanInterest | undefined {
  const plan = new URLSearchParams(search).get('plan')?.trim().toLowerCase();
  return (PLAN_INTERESTS as readonly string[]).includes(plan ?? '') ? (plan as PlanInterest) : undefined;
}

const isOwnHost = (host: string, ownHost: string) =>
  host === ownHost || host === 'bearri.co' || host.endsWith('.bearri.co');
/** sign-in round trips, never where someone heard about Bearri: Google's consent screen, Clerk's own pages */
const isSignInHop = (host: string) =>
  host === 'accounts.google.com' || host === 'accounts.dev' || host.endsWith('.accounts.dev') ||
  host === 'clerk.com' || host.endsWith('.clerk.com');

/** An outside page that linked here, as origin + path only (no query, no fragment), or nothing. */
export function externalReferrer(referrer: string, ownOrigin: string): string | undefined {
  if (!referrer) return undefined;
  try {
    const ref = new URL(referrer);
    if (ref.protocol !== 'http:' && ref.protocol !== 'https:') return undefined;
    if (isOwnHost(ref.hostname, new URL(ownOrigin).hostname) || isSignInHop(ref.hostname)) return undefined;
    return `${ref.origin}${ref.pathname}`.slice(0, REFERRER_MAX);
  } catch {
    return undefined;
  }
}

function cleanSource(raw: unknown): WaitlistSource | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const src = raw as Record<string, unknown>;
  const out: WaitlistSource = {};
  for (const key of UTM_KEYS) {
    const v = src[key];
    if (typeof v === 'string' && v.trim()) out[key] = v.trim().slice(0, UTM_MAX);
  }
  if (typeof src.referrer === 'string' && src.referrer.trim()) out.referrer = src.referrer.trim().slice(0, REFERRER_MAX);
  return Object.keys(out).length ? out : undefined;
}

/**
 * Note where this visit came from, once per tab, on the first public page it opens. Signed-in pages (/app) are
 * skipped. A first page with nothing to say still leaves an empty note ({}), so a later full load in the same tab
 * (a Google sign-in coming back, say) can't fill it in with somewhere the person never came from.
 * Storage can be blocked (private windows, strict settings): then nothing is kept and nothing breaks.
 */
export function captureArrivalSource(
  loc: Pick<Location, 'pathname' | 'search' | 'origin'> = window.location,
  referrer: string = typeof document !== 'undefined' ? document.referrer : '',
): void {
  try {
    if (loc.pathname === '/app' || loc.pathname.startsWith('/app/')) return;
    const store = window.sessionStorage;
    if (store.getItem(SOURCE_KEY) !== null) return;
    const params = new URLSearchParams(loc.search);
    const found: Record<string, string> = {};
    for (const key of UTM_KEYS) {
      const v = params.get(key);
      if (v) found[key] = v;
    }
    const ref = externalReferrer(referrer, loc.origin);
    if (ref) found.referrer = ref;
    store.setItem(SOURCE_KEY, JSON.stringify(cleanSource(found) ?? {}));
  } catch {
    // no storage: the waitlist simply goes without a source
  }
}

export function readArrivalSource(): WaitlistSource | undefined {
  try {
    const raw = window.sessionStorage.getItem(SOURCE_KEY);
    return raw ? cleanSource(JSON.parse(raw)) : undefined;
  } catch {
    return undefined;
  }
}
